import { SECURE_LAUNCH_PANEL, consoleRoutes, type SettingsPanel } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Icon, Skeleton, cn } from "@sagot/ui";
import { Fragment, type ReactNode } from "react";
import { useParams } from "react-router";
import { usePermissions } from "../../../session/permissions";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { ConnectorsNavLink } from "./connectors/settings-nav-link";
import { ApiKeysPanel } from "./panels/api-keys-panel";
import { AttributesPanel } from "./panels/attributes-panel";
import { AuditPanel } from "./panels/audit-panel";
import { DomainsPanel } from "./panels/domains-panel";
import { IntegrationsPanel } from "./panels/integrations-panel";
import { NotificationsPanel } from "./panels/notifications-panel";
import { OrganizationPanel } from "./panels/organization-panel";
import { PanelForbidden } from "./panels/panel-kit";
import { PrivacyPanel } from "./panels/privacy-panel";
import { ProfilePanel } from "./panels/profile-panel";
import { SecurityPanel } from "./panels/security-panel";
import { SecureLaunchPage } from "./secure-launch/secure-launch-page";
import { useLaunchSettings } from "./secure-launch/use-launch-settings";
import { SlaPanel } from "./panels/sla-panel";
import { WebhooksPanel } from "./panels/webhooks-panel";
import { NavDirection, PANEL_DIRECTION } from "./settings-direction";
import { PANEL_ICONS, panelAllowed, resolvePanel, visiblePanels } from "./settings-model";

const PANELS: Readonly<Record<SettingsPanel, () => ReactNode>> = {
  org: () => <OrganizationPanel />,
  profile: () => <ProfilePanel />,
  api: () => <ApiKeysPanel />,
  webhooks: () => <WebhooksPanel />,
  integrations: () => <IntegrationsPanel />,
  "secure-launch": () => <SecureLaunchPage />,
  security: () => <SecurityPanel />,
  privacy: () => <PrivacyPanel />,
  audit: () => <AuditPanel />,
  notifications: () => <NotificationsPanel />,
  domains: () => <DomainsPanel />,
  attributes: () => <AttributesPanel />,
  sla: () => <SlaPanel />,
};

const NAV_LINK =
  "flex shrink-0 items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm font-semibold text-fg-muted hover:bg-sunken hover:text-fg aria-[current=page]:bg-surface aria-[current=page]:text-fg aria-[current=page]:shadow-xs aria-[current=page]:ring-1 aria-[current=page]:ring-line";

/** Connectors sit right after Webhooks (the two outbound directions side by side); without Webhooks they close the list. */
function SettingsNav({ active, panels, showRoles, showConnectors }: { active: SettingsPanel; panels: readonly SettingsPanel[]; showRoles: boolean; showConnectors: boolean }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const connectorsAfterWebhooks = panels.includes("webhooks");
  return (
    <nav className="lg:sticky lg:top-20 lg:w-56 lg:shrink-0" aria-label={t("settings.navLabel")}>
      <ul className="-mx-4 flex gap-1 overflow-x-auto px-4 pb-1 lg:mx-0 lg:flex-col lg:overflow-visible lg:px-0 lg:pb-0">
        {panels.map((panel) => {
          const direction = PANEL_DIRECTION[panel];
          return (
            <Fragment key={panel}>
              <li className="shrink-0">
                <AppLink href={consoleRoutes.settings(summary.slug, panel)} className={NAV_LINK} aria-current={panel === active ? "page" : undefined}>
                  <Icon name={PANEL_ICONS[panel]} className="size-4" />
                  <span className="min-w-0">
                    {t(`settings.panels.${panel}`)}
                    {direction ? <NavDirection label={t(`settings.direction.${direction}`)} direction={direction} /> : null}
                  </span>
                </AppLink>
              </li>
              {panel === "webhooks" && showConnectors ? <ConnectorsNavLink /> : null}
            </Fragment>
          );
        })}
        {showRoles ? (
          <li className="shrink-0">
            <AppLink href={consoleRoutes.roles(summary.slug)} className={NAV_LINK}>
              <Icon name="shield-check" className="size-4" />
              {t("settings.panels.roles")}
            </AppLink>
          </li>
        ) : null}
        {showConnectors && !connectorsAfterWebhooks ? <ConnectorsNavLink /> : null}
      </ul>
    </nav>
  );
}

function NavSkeleton() {
  return (
    <div className="flex gap-1 lg:w-56 lg:shrink-0 lg:flex-col" aria-hidden="true">
      {Array.from({ length: 6 }, (_, i) => (
        <Skeleton key={i} className="h-9 w-28 lg:w-full" />
      ))}
    </div>
  );
}

/** `/w/:ws/settings/:panel?` — workspace, profile, developer, security and privacy settings. */
export function SettingsPage() {
  const { t } = useTranslation();
  const { panel: param } = useParams();
  const permissions = usePermissions();
  const active = resolvePanel(param);
  const can = permissions.can;
  const allowed = panelAllowed(active, can);
  // The Secure launch entry exists only where the platform flag is on. `GET /v1/launch-settings` answers 200 with `enabled`,
  // so a workspace outside the rollout never makes a request that fails (BUG-M29-AICTX-001, BUG-M29-PRIVACY-001).
  const secureLaunch = useLaunchSettings(can("settings.view")).data?.enabled === true;
  const panels = visiblePanels(can).filter((panel) => panel !== SECURE_LAUNCH_PANEL || secureLaunch);
  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight">{t("settings.title")}</h1>
        <p className="mt-1 text-fg-muted">{t("settings.subtitle")}</p>
      </div>
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        {permissions.isPending ? <NavSkeleton /> : <SettingsNav active={active} panels={panels} showRoles={can("team.view")} showConnectors={can("connectors.view")} />}
        <div className={cn("min-w-0 flex-1 space-y-6")} data-panel={active}>
          {permissions.isPending ? <Skeleton className="h-96 w-full" /> : allowed ? PANELS[active]() : <PanelForbidden />}
        </div>
      </div>
    </div>
  );
}
